import { useEffect, useMemo, useState } from "react";
import { Ban, MapPin, Save, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function OperatorRestrictions() {
    const [properties, setProperties] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        Promise.all([
            api.get("/operator/property-restriction-options"),
            api.get("/operator/property-restrictions"),
        ]).then(([options, restrictions]) => {
            setProperties(options.data || []);
            setSelected(new Set(restrictions.data?.property_ids || []));
        }).catch((err) => setError(formatApiError(err))).finally(() => setLoading(false));
    }, []);

    const visible = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return properties;
        return properties.filter((property) =>
            [property.code, property.name, property.city].some((value) =>
                String(value || "").toLowerCase().includes(query),
            ),
        );
    }, [properties, search]);

    const toggle = (propertyId) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(propertyId)) next.delete(propertyId);
            else next.add(propertyId);
            return next;
        });
        setMessage("");
    };

    const save = async () => {
        setSaving(true);
        setError("");
        setMessage("");
        try {
            await api.put("/operator/property-restrictions", { property_ids: Array.from(selected) });
            setMessage("Limitaciones guardadas. Estas viviendas ya no podrán asignarse a tu usuario.");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="operator-restrictions-page">
            <div>
                <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">Preferencias operativas</p>
                <h2 className="font-display text-3xl font-bold text-zentra-ink mt-1">Limitaciones de viviendas</h2>
                <p className="text-sm text-zentra-muted mt-1">Selecciona domicilios en los que no puedes prestar servicio. El coordinador no podrá asignártelos.</p>
            </div>

            <div className="zentra-card-static p-4 flex flex-wrap items-center gap-3">
                <label className="zentra-input flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search size={14} className="text-zentra-muted" />
                    <input className="bg-transparent border-0 outline-none w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar código, vivienda o municipio" />
                </label>
                <span className="zentra-chip bg-red-50 text-red-800 border-red-200"><Ban size={12} /> {selected.size} restringidas</span>
                <button className="zentra-btn-primary" onClick={save} disabled={saving} data-testid="save-operator-restrictions">
                    <Save size={14} /> {saving ? "Guardando…" : "Guardar limitaciones"}
                </button>
            </div>

            {message && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{message}</div>}
            {error && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}

            {loading ? <div className="zentra-skeleton h-72" /> : (
                <div className="zentra-card-static divide-y divide-zentra-border max-h-[620px] overflow-y-auto">
                    {visible.map((property) => {
                        const checked = selected.has(property.id);
                        return (
                            <label key={property.id} className={`flex items-center gap-3 p-3 cursor-pointer transition ${checked ? "bg-red-50" : "hover:bg-zentra-bg/40"}`}>
                                <input type="checkbox" checked={checked} onChange={() => toggle(property.id)} className="w-4 h-4 accent-red-700" data-testid={`restriction-${property.id}`} />
                                <MapPin size={15} className={checked ? "text-red-700" : "text-zentra-muted"} />
                                <span className="font-mono-zentra text-xs text-zentra-muted w-24 shrink-0">{property.code}</span>
                                <span className="text-sm font-medium text-zentra-ink flex-1">{property.name || "Sin nombre"}</span>
                                <span className="text-xs text-zentra-muted">{property.city || ""}</span>
                            </label>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
